import {
  MarketState,
  OpenerRuleState,
  OpeningPageState
} from '../types/entry.types';

/**
 * What actually rides `opening_state_update`: only the fields that changed for
 * that pair since the pod last sent it, plus `pairId`.
 *
 * A full `OpeningPageState` is assignable to this, so the seed and the REST
 * snapshot keep their exact shape — only the delta path narrows. Mirrors the
 * backend's `OpeningPageStateDelta` (`services/position-opener/src/
 * engine-boundary/display-types.ts`); the two must be changed together.
 *
 * `undefined` is the absence of news and `null` is a value: an absent `rule`
 * means "no news about the rule", `rule: null` means "this pair has no rule".
 */
export type OpeningPageStateDelta = { pairId: string } & Partial<
  Omit<OpeningPageState, 'pairId' | 'market' | 'rule'>
> & {
    market?: Partial<MarketState>;
    rule?: Partial<OpenerRuleState> | null;
    /**
     * The pod is telling us this frame is a COMPLETE state, not a delta: it
     * had no memory of this pair, or it re-keyed it. Absent on every ordinary
     * delta, and absent on EVERY frame from a pod that predates it — which is
     * why `frameIsSelfSufficient` still has a fallback.
     */
    full?: true;
  };

/**
 * When the transitional fallback in `frameIsSelfSufficient` must be gone.
 *
 * That fallback exists only because this half of the change deploys BEFORE
 * core#534, so for one release the pod does not stamp `full`. It is guarded by
 * a comment telling the next person not to simplify it — and a comment with no
 * forcing function is a comment that outlives its reason. The test on this
 * constant goes red on the date, which is the repo's usual way of making a
 * temporary thing actually temporary.
 */
export const FULL_FLAG_FALLBACK_RETIRE_BY = '2026-11-26';

/**
 * Rebuild the FULL state a delta describes, on top of the last full state we
 * hold for that pair.
 *
 * This is the ONE place the "omitted = keep what you had" contract is applied,
 * and applying it HERE — on the raw wire shape, before `projectToLegacy` — is
 * the whole point. The alternative is to teach each of the ~60 fields of the
 * projection to tell "no news" from "there is none", which is 60 chances to
 * write `r?.x ?? null` and silently turn the first into the second. Everything
 * downstream (`projectToLegacy`, `mergePreservedFields`, `entryStateEqual`)
 * keeps receiving a complete state and needs to know nothing about deltas.
 *
 * With no base the delta IS the full state: the pod only omits a field after
 * it has sent it, and it forgets every pair on a client seed
 * (`onClientSeeded`) and on eviction (`forgetSentOutside`). A payload that is
 * nonetheless partial is caught downstream by `frameIsSelfSufficient`, which drops it
 * rather than store a half-blank row.
 */
export function mergeOpeningPageState(
  prev: OpeningPageState | undefined,
  delta: OpeningPageStateDelta
): OpeningPageState {
  // `full` means REPLACE, in the merge exactly as in the guard.
  //
  // The pod stamps it on the two paths that `return next` outright — no
  // previous state, and a rule re-key, where its own comment says "the value
  // it holds under the OLD key is not a base the new one can build on". Those
  // paths skip `diffBlock`, so they never get its disappearing-key treatment
  // (`asWireValue`, which turns "was in prev, absent from next" into an
  // explicit `null`). Merging them would inherit, from the OLD rule, every key
  // the new one omits — and the enricher genuinely omits keys:
  // `buildAppliedSimFields` returns `{ hasPendingSim: false }` with no
  // `simTna` / `appliedSim*` at all, and `currentSumOfAsks` is
  // `sumPathA ?? undefined`.
  //
  // Merging them is also a REGRESSION against the code this replaced, where a
  // complete frame with `simTna` absent projected `undefined` and the value
  // cleared. And with `OPENER_FIELD_DELTA=off` every frame carries the flag,
  // so without this the kill switch restores the old VOLUME but not the old
  // meaning.
  if (!prev || delta.full) {
    // Stripped HERE, because this is the path the flag actually enters the
    // cache through: the caller stores whatever this returns, and on the
    // replace path that is the frame itself. Stripping only the delta's
    // contribution to a MERGE (below) leaves the flag on the stored state and
    // `{ ...prev }` propagates it forever.
    const { full: _full, ...state } = delta;
    return state as OpeningPageState;
  }

  // The same strip on the merge path. With the replace path above already
  // clearing it, this is remote defence — reachable only from a pod sending a
  // falsey `full`, which the `full?: true` type forbids — but the invariant it
  // states is the load-bearing one: `full` describes a FRAME, not a state, and
  // riding into the cache it would survive every later merge and sit on that
  // pair's base forever. `OpeningPageState` does not even
  // declare it. Inert today because the guard reads `delta.full`, but one
  // word away from disaster: switching that to `merged.full` — the natural
  // move, since `merged` is what everything else consumes — would make every
  // partial frame for any pair that ever received a complete one pass as
  // self-sufficient. Forever, and for all of them.
  const { market, rule, full: _full, ...scalars } = delta;

  return {
    ...prev,
    ...scalars,
    market: market ? { ...prev.market, ...market } : prev.market,
    // Three cases, and they are NOT the same: absent = no news (keep),
    // `null` = the pair lost its rule, an object = field news on the rule we
    // already hold. A rule that appears or is replaced re-keys the row, so the
    // pod sends the whole state for it and `prev.rule` is never a stale base
    // for a different rule — but if it is missing anyway, take the incoming
    // block whole rather than merge onto nothing.
    rule:
      rule === undefined
        ? prev.rule
        : rule === null
          ? null
          : prev.rule
            ? { ...prev.rule, ...rule }
            : (rule as OpenerRuleState)
  };
}
